import { useId, useState } from "react";

/**
 * Ad spend calculator: the reader enters their own numbers, nothing is pre-filled.
 * Placement: blog post, section "SEO or paid ads first?", directly after the comparison table.
 * Uses shadcn/Tailwind theme tokens so it inherits the site's colours.
 */

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function toNumber(value: string): number | null {
  const n = Number(value.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function AdSpendCalculator() {
  const cpcId = useId();
  const clicksId = useId();
  const [cpc, setCpc] = useState("");
  const [clicks, setClicks] = useState("");

  const cpcNum = toNumber(cpc);
  const clicksNum = toNumber(clicks);
  const monthly = cpcNum !== null && clicksNum !== null ? cpcNum * clicksNum : null;

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section
      aria-labelledby="ad-spend-title"
      className="not-prose my-10 rounded-xl border border-border bg-card p-6 text-card-foreground sm:p-8"
    >
      <h3 id="ad-spend-title" className="text-xl font-semibold">
        What would those clicks cost in ads?
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Use the cost per click from your own Google Ads account or Keyword Planner.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={cpcId} className="text-sm font-medium">
            Average cost per click (USD)
          </label>
          <input
            id={cpcId}
            inputMode="decimal"
            placeholder="e.g. 45"
            value={cpc}
            onChange={(e) => setCpc(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={clicksId} className="text-sm font-medium">
            Clicks you want each month
          </label>
          <input
            id={clicksId}
            inputMode="numeric"
            placeholder="e.g. 200"
            value={clicks}
            onChange={(e) => setClicks(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div aria-live="polite" className="mt-6">
        {monthly !== null ? (
          <div className="grid gap-4 rounded-lg bg-muted p-5 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Monthly ad spend</p>
              <p className="text-2xl font-semibold">{usd.format(monthly)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Yearly ad spend</p>
              <p className="text-2xl font-semibold">{usd.format(monthly * 12)}</p>
            </div>
            <p className="text-sm text-muted-foreground sm:col-span-2">
              That spend stops producing clicks the day the budget stops. Organic clicks have no per-click cost, but SEO
              has its own monthly cost and takes months to build. Compare both before deciding.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Enter both numbers to see the cost.</p>
        )}
      </div>
    </section>
  );
}
